import React, { useState } from 'react';
import { ACTIONS_COL_WIDTH, Alert, Badge, Button, Card, Col, Row, RowActions, Table } from '@/components/ui';
import { ExpiryDate } from '../../../components/ExpiryIndicator';
import { VENDOR_DOC_TYPE_LABELS, Vendor, vendorService } from '../../../services/vendorService';
import VendorDocumentModal from './VendorDocumentModal';
import { shortDate } from '../utils/format';
import { confirmAction } from '@/components/ui';

interface Props {
  vendor: Vendor;
  canManage: boolean;
  onChanged: () => Promise<void> | void;
}

const VendorDocumentsPanel: React.FC<Props> = ({ vendor, canManage, onChanged }) => {
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async (documentId: number, fileName: string) => {
    // Destructive and unrecoverable from the UI. Every other table in the app confirms first
    // (OtherDocumentList, SubcontractorsMasterTable, HiredEquipmentMasterTable), and
    // ACTION_REGISTRY marks `delete` as caller-confirmed.
    if (!(await confirmAction({ title: `Remove "${fileName}"?`, body: 'This cannot be undone.', confirmLabel: 'Remove', destructive: true }))) return;
    try {
      setError(null);
      await vendorService.deleteDocument(documentId);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  const compliance = vendor.compliance;

  return (
    <>
      <Card className="tw-mb-3">
        <Card.Body>
          <h4 className="tw-text-base tw-mb-3">Compliance summary</h4>
          <Row>
            <Col md={6}>
              <div className="tw-text-sm tw-text-content-muted">WCB Clearance expires</div>
              <ExpiryDate
                value={compliance?.wcbExpiresAt ?? null}
                expired={compliance?.wcbExpired}
                missing={compliance?.wcbMissing}
              />
            </Col>
            <Col md={6}>
              <div className="tw-text-sm tw-text-content-muted">Liability insurance expires</div>
              <ExpiryDate
                value={compliance?.insuranceExpiresAt ?? null}
                expired={compliance?.insuranceExpired}
                missing={compliance?.insuranceMissing}
              />
            </Col>
          </Row>
          {compliance?.hasIssue && (
            <Alert variant="warning" className="tw-mt-3 tw-mb-0">
              This vendor cannot be activated until both WCB and liability insurance are present and current.
              Existing work is not blocked – this is a warning, not a stop.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" className="tw-mb-3">{error}</Alert>}

      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <h6 className="tw-mb-0">Attached files</h6>
        {canManage && (
          <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-lg tw-mr-1" />
            Add Document
          </Button>
        )}
      </div>

      <Card>
        <Card.Body className="tw-p-0">
          <Table responsive className="tw-mb-0">
            <thead>
              <tr>
                <th>Type</th>
                <th>File</th>
                <th>Issued</th>
                <th>Expires</th>
                <th>Uploaded By</th>
                {canManage && (
                  <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {(vendor.documents ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={canManage ? 6 : 5}
                    className="tw-text-center tw-py-4 tw-text-content-muted"
                  >
                    <i className="bi bi-paperclip tw-text-3xl tw-block tw-mb-2" />
                    No documents attached.
                    {canManage && (
                      <div>
                        <small>
                          Use &ldquo;Add Document&rdquo; to attach a quote, WCB clearance,
                          insurance certificate or any general document.
                        </small>
                      </div>
                    )}
                  </td>
                </tr>
              )}
              {(vendor.documents ?? []).map((doc) => {
                const expired = !!doc.expiresAt && new Date(doc.expiresAt) < new Date();
                return (
                  <tr key={doc.id}>
                    <td>
                      {/* Primary marks the two compliance documents; everything else is grey. */}
                      <Badge bg={doc.docType === 'WCB' || doc.docType === 'LIABILITY_INSURANCE' ? 'primary' : 'secondary'}>
                        {VENDOR_DOC_TYPE_LABELS[doc.docType]}
                      </Badge>
                    </td>
                    <td>
                      <a
                        href={vendorService.getDocumentDownloadUrl(doc.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doc.fileName}
                      </a>
                    </td>
                    <td>{shortDate(doc.issuedAt)}</td>
                    <td>
                      <ExpiryDate value={doc.expiresAt ?? null} expired={expired} />
                    </td>
                    <td>
                      {doc.uploadedBy ? `${doc.uploadedBy.name} ${doc.uploadedBy.lastName}` : '–'}
                    </td>
                    {canManage && (
                      <td className="tw-text-right">
                        <RowActions
                          actions={[
                            {
                              kind: 'download',
                              label: 'Open Document',
                              onClick: () =>
                                window.open(
                                  vendorService.getDocumentDownloadUrl(doc.id),
                                  '_blank',
                                  'noopener'
                                ),
                            },
                            {
                              kind: 'delete',
                              label: 'Remove Document',
                              onClick: () => handleDelete(doc.id, doc.fileName),
                            },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <VendorDocumentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        vendorId={vendor.id}
        onSaved={onChanged}
      />
    </>
  );
};

export default VendorDocumentsPanel;
